<template>
  <div
    class="add-to-cart-form"
    :class="{
      'add-to-cart-form--minimal': minimal,
    }"
  >
    <div
      v-if="minimal"
      class="atcf-actions"
    >
      <fn-link
        v-if="hasSizeTablesOrMeasureInfo"
        class="atcf-modal-sizes-and-measurements-link"
        no-arrow
        inline
        icon
        bold
        underline
        @click.prevent="$emit('modalSizesAndMeasurementsOpened')"
      >
        <fn-icon
          icon="ruler"
          color="black"
          size="xs"
        />
        {{ $t('product.sizesAndMeasurements.title') }}
      </fn-link>
      <fn-vuex-button-add-to-cart
        class="atcf-action-cart"
        :code="code"
        :stock="stock"
        :quantity="quantity"
        @add-to-cart-overlay="(payload) => (!isAppview ? $emit('addToCartOverlay', payload) : null)"
      />
    </div>
    <template v-else>
      <div class="atcf-variants">
        <fn-product-variant-selector
          id="atcf-first-product-variant-selector"
          v-model:value="selectedFirstBaseOptionUrl"
          :base-option="firstBaseOption"
          :is-slider="!secondBaseOption?.options?.length"
          :simple-dropdown="!!secondBaseOption?.options?.length"
          @input="handleChangeVariant"
          @change-tab="(tab) => $emit('changeTab', tab)"
        />

        <fn-product-variant-selector
          id="atcf-second-product-variant-selector"
          v-model:value="selectedSecondBaseOptionUrl"
          :base-option="secondBaseOption"
          :is-slider="!!secondBaseOption?.options?.length"
          @input="handleChangeVariant"
          @change-tab="(tab) => $emit('changeTab', tab)"
        />
      </div>

      <fn-link
        v-if="hasSizeTablesOrMeasureInfo"
        :class="[
          'atcf-modal-sizes-and-measurements-link',
          { 'atcf-modal-sizes-and-measurements-link--with-variants': !!variantSelectors?.length },
        ]"
        no-arrow
        inline
        icon
        bold
        underline
        @click.prevent="$emit('modalSizesAndMeasurementsOpened')"
      >
        <fn-icon
          icon="ruler"
          color="black"
          size="xs"
        />
        {{ $t('product.sizesAndMeasurements.title') }}
      </fn-link>

      <fn-panel
        background-color="grey-lighter"
        medium-padding
        border-radius-rebrand
      >
        <div class="atcf-actions">
          <fn-friends-price-box
            v-if="!!showFriendsPriceBox"
            class="atcf-action-friends-price-box"
            :friends-price="pricing.friendsPrice?.formattedValue"
            :friends-price-per-unit="pricing.friendsPricePerUnit?.formattedValue"
          />
          <fn-product-pricing
            class="atcf-action-pricing"
            large
            :pricing="pricing"
            display-tax-info
            :badges="badges"
            @change-tab="(tab) => $emit('changeTab', tab)"
          />
          <fn-quantity
            v-model:value="quantity"
            class="atcf-action-quantity"
            :max="maxQuantity"
          />
          <div class="atcf-action-buttons">
            <fn-vuex-button-add-to-cart
              class="atcf-action-cart"
              :code="code"
              :stock="stock"
              :quantity="quantity"
              @add-to-cart-overlay="(payload) => (!isAppview ? $emit('addToCartOverlay', payload) : null)"
            />
            <div class="atcf-action-wishlist">
              <fn-wishlist-button
                atcf-style
                :product-code="code"
              />
            </div>
          </div>
        </div>
      </fn-panel>
    </template>
  </div>
</template>

<script>
import { getFirstBaseOption, getSecondBaseOption } from '@/utils/product';

export default defineComponent({
  props: {
    variantSelectors: {
      type: Array,
      default: () => [],
    },
    pricing: {
      type: Object,
      required: true,
    },
    stock: {
      type: Object,
      required: true,
    },
    code: {
      type: [String, Number],
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
    maxQuantity: {
      type: Number,
      default: 1,
    },
    minimal: {
      type: Boolean,
    },
    shippingInformation: {
      type: Object,
      default: () => ({}),
    },
    marketplaceArticle: {
      type: Boolean,
    },
    clickAndCollectArticle: {
      type: Boolean,
    },
    hasSizeTablesOrMeasureInfo: {
      type: Boolean,
    },
    badges: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['changeTab', 'modalSizesAndMeasurementsOpened', 'addToCartOverlay', 'changeVariant'],
  data() {
    return {
      quantity: 1,
      selectedFirstBaseOptionUrl: this.url,
      selectedSecondBaseOptionUrl: this.url,
      mounted: false,
    };
  },
  computed: {
    ...mapState(useUserStore, {
      isAuthenticated: 'getIsAuthenticated',
      hasFriendsBenefit: 'getHasFriendsBenefit',
    }),

    ...mapState(useMainStore, {
      isAppview: 'isAppview',
    }),

    firstBaseOption() {
      return getFirstBaseOption(this.variantSelectors);
    },

    secondBaseOption() {
      return getSecondBaseOption(this.variantSelectors);
    },

    showFriendsPriceBox() {
      return !this.hasFriendsBenefit && this.pricing.friendsPrice;
    },
  },
  mounted() {
    this.mounted = true;
  },
  methods: {
    handleChangeVariant(url) {
      this.$emit('changeVariant', {
        url,
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.add-to-cart-form {
  position: relative;
  display: block;
  margin-top: $pad;

  @include bp(m) {
    margin-top: $space;
  }

  &.add-to-cart-form--minimal {
    margin-top: 0;
  }

  .atcf-variants {
    .product-variant-selector {
      margin-bottom: $pad;
    }
  }

  .atcf-actions {
    display: grid;
    grid-template-rows: auto auto auto auto;
    row-gap: $pad;

    .atcf-shipping-information,
    .atcf-stock-information {
      width: 100%;

      :deep(.list) {
        row-gap: math.div($pad, 2);
      }
    }

    .atcf-shipping-information {
      display: none;

      @include bp(m) {
        display: block;
      }
    }

    .atcf-action-buttons {
      display: grid;
      grid-template-columns: 1fr auto;

      .atcf-action-cart {
        margin: 0 math.div($grid-margin-width, 2) 0 0;

        :deep(.button-add-to-cart),
        :deep(.button) {
          width: 100%;
        }
      }

      .atcf-action-wishlist {
        margin: 0;
      }
    }
  }

  .atcf-action-quantity {
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: 100%;
    background-color: $color-general-background-neutral-default;

    @include bp(s) {
      flex-basis: span-columns(3);
      align-self: flex-end;
    }
  }

  .atcf-modal-sizes-and-measurements-link {
    padding-bottom: $pad;

    &.atcf-modal-sizes-and-measurements-link--with-variants {
      padding: 0 0 $pad $pad;
    }

    @include bp(m) {
      display: none;
    }
  }
}
</style>
